import { Phone, Mail, AlertTriangle, User } from "lucide-react";
import useFeatures from "../../hooks/useRedFlag";
import { useAuth } from "../../context/authContext";
import GlassCard from "../ui/GlassCard";
import LoadingState from "../ui/LoadingState";

// Uses the same hook/API as the Alert component (useFeatures -> redFlagAttacks).
// If redFlagAttacks.alerts exists (AIIMS), show per-user alerts.
// Otherwise fall back to one row per label from labelCounts.
const LIST_KEYS = ["alerts", "users", "cases", "details", "records", "list"];

const pick = (obj, keys) => {
  for (const k of keys) if (obj?.[k]) return obj[k];
  return "";
};

const normalizeAlerts = (redFlag) => {
  if (!redFlag) return [];

  // Per-person list (when the backend sends it)
  const raw = pick(redFlag, LIST_KEYS);
  if (Array.isArray(raw)) {
    return raw.map((a, i) => ({
      id: a._id || a.id || i,
      name: pick(a, ["name", "userName", "username", "fullName", "alias"]),
      userEmail: pick(a, ["userEmail", "email"]),
      alertDetail: pick(a, ["label", "alertDetail", "issue", "reason", "type"]),
      createdAt: pick(a, ["createdAt", "date", "timestamp"]),
      emergencyName: pick(a, ["emergencyContactName", "emergencyName"]),
      emergencyNumber: pick(a, ["emergencyContactNumber", "emergencyNumber", "emergency_phone"]),
      // Use your real email field name here (it must NOT be the number field)
      emergencyEmail: pick(a, ["emergencyContactEmail", "emergencyEmail", "emergency_email", "emergencyMail"]),
    }));
  }

  // Fallback: one row per label from labelCounts
  return Object.entries(redFlag.labelCounts || {}).map(([label, count]) => ({
    id: label,
    name: "",
    userEmail: "",
    alertDetail: label,
    count,
    createdAt: "",
    emergencyName: "",
    emergencyNumber: "",
    emergencyEmail: "",
  }));
};

const useAlertsGenerated = (code) => {
  const { features, loading, error } = useFeatures(code);
  const alerts = normalizeAlerts(features?.redFlagAttacks);
  return { alerts, loading, error };
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d)
    ? ""
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// One contact field: icon tile + small label + value. Link when `href` is given.
const ContactRow = ({ label, href, icon: Icon, value }) => {
  const body = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#ef5da8]/10 text-[#ef5da8]">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {label}
        </span>
        <span
          className={`block break-words text-sm font-medium ${
            value ? "text-slate-800 dark:text-white/90" : "text-slate-400 dark:text-white/40"
          }`}
        >
          {value || "Not provided"}
        </span>
      </span>
    </>
  );

  const base =
    "flex items-center gap-3 rounded-xl bg-white/60 p-2.5 dark:bg-white/[0.03]";

  return href && value ? (
    <a href={href} className={`${base} transition-colors hover:bg-[#ef5da8]/5`}>
      {body}
    </a>
  ) : (
    <div className={base}>{body}</div>
  );
};

const AlertsGenerated = ({ name = "Alerts Generated" }) => {
  const { code } = useAuth();
  const { alerts, loading, error } = useAlertsGenerated(code);

  return (
    <GlassCard
      accent="magenta"
      className="flex h-full min-h-0 flex-col gap-4 text-slate-900 dark:text-white"
    >
      <div className="flex items-center justify-between">
        <h1 className="font-['Space_Grotesk',_sans-serif] text-[22px] font-semibold">{name}</h1>
        {!loading && !error && alerts.length > 0 && (
          <span className="rounded-full bg-[#ef5da8]/10 px-3 py-1 text-xs font-semibold text-[#ef5da8]">
            {alerts.length} {alerts.length === 1 ? "alert" : "alerts"}
          </span>
        )}
      </div>

      {loading && <LoadingState rows={3} />}

      {!loading && (error || alerts.length === 0) && (
        <p className="text-sm text-slate-400 dark:text-white/40">No alerts have been generated yet.</p>
      )}

      {!loading && !error && alerts.length > 0 && (
        <ul className="flex max-h-[420px] min-h-0 flex-1 flex-col gap-3 overflow-y-auto custom-scrollbar pr-1 lg:max-h-none">
          {alerts.map((a) => (
            <li
              key={a.id}
              className="rounded-2xl border border-black/[0.06] bg-black/[0.03] p-4 dark:border-white/[0.06] dark:bg-white/[0.04]"
            >
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="min-w-0 max-w-full">
                  {a.name && <p className="truncate text-base font-semibold">{a.name}</p>}
                  {a.createdAt && (
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      {formatDate(a.createdAt)}
                    </p>
                  )}
                </div>
                <span className="flex max-w-full shrink-0 items-center gap-1.5 rounded-full bg-[#FF5C7A]/10 px-3 py-1 text-xs font-semibold text-[#FF5C7A]">
                  <AlertTriangle size={12} />
                  {a.alertDetail || "Alert"}
                  {a.count ? ` · ${a.count}` : ""}
                </span>
              </div>

              {(a.emergencyName || a.emergencyNumber || a.emergencyEmail) && (
                <div className="mt-3 grid grid-cols-1 gap-2 border-t border-black/[0.06] pt-3 dark:border-white/[0.06] sm:grid-cols-3">
                  <ContactRow
                    label="Contact name"
                    icon={User}
                    value={a.emergencyName}
                  />
                  <ContactRow
                    label="Emergency number"
                    href={`tel:${a.emergencyNumber}`}
                    icon={Phone}
                    value={a.emergencyNumber}
                  />
                  <ContactRow
                    label="Emergency email"
                    href={`mailto:${a.emergencyEmail}`}
                    icon={Mail}
                    value={a.emergencyEmail}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
};

export default AlertsGenerated;